"use client";

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Fadeup from "../fadeup/Fadeup";

const base_url = process.env.REACT_APP_BASE_URL;

const AuctionDetailsComp = () => {
  const { id } = useParams();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [highestBid, setHighestBid] = useState(null);

  // Dialog states
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [bidAmount, setBidAmount] = useState("");
  const [bidStatus, setBidStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isLoggedIn = !!localStorage.getItem("token");

  useEffect(() => {
    if (!id) return;

    const token = localStorage.getItem("token");

    // Fetch Auction Details
    const fetchAuctionById = async () => {
      try {
        const res = await fetch(`${base_url}/api/auction/getAuctionDetails/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const json = await res.json();
        if (!res.ok) throw new Error(json.message || "Failed to fetch auction details");
        setAuction(json.data);

        // Fetch current highest bid
        const bidRes = await fetch(`${base_url}/api/live-auction/getHighestBid?auctionId=${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const bidJson = await bidRes.json();
        if (bidRes.ok && bidJson.data) setHighestBid(bidJson.data);
      } catch (err) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchAuctionById();

    // console.log(token);
    

    // ✅ Setup Server-Sent Events (SSE)
    const eventSource = new EventSource(
      `${base_url}/api/live-auction/subscribe?auctionId=${id}&token=${token}`
    );

    eventSource.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        // Debug log (optional)
        // console.log("SSE Event Received:", msg);

        if (msg.type === "connected") {
          console.log("✅ Connected to live bid updates");
        }

        if (msg.type === "new_bid" && msg.data?.auction_id === Number(id)) {
          setHighestBid({
            current_highest_bid: msg.data.current_highest_bid,
            user_name: "Anonymous",
            updatedAt: msg.data.updatedAt,
          });
        }
      } catch (err) {
        console.error("SSE parse error:", err);
      }
    };

    eventSource.onerror = (err) => {
      console.warn("SSE connection lost. Reconnecting...", err);
    };

    // Cleanup on unmount
    return () => {
      eventSource.close();
    };
  }, [id]);

  // ✅ Handle Place Bid
  const handleBidSubmit = async () => {
    if (!bidAmount) {
      setBidStatus({ type: "error", message: "Please enter a bid amount." });
      return;
    }

    const token = localStorage.getItem("token");
    setSubmitting(true);
    setBidStatus(null);

    try {
      const res = await fetch(`${base_url}/api/live-auction/placeBid`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          auctionId: Number(id),
          bidAmount: Number(bidAmount),
        }),
      });

      const json = await res.json();

      if (res.status === 200) {
        setBidStatus({ type: "success", message: json.message });
        setBidAmount("");
        setTimeout(() => setShowBidDialog(false), 1500);
      } else {
        setBidStatus({ type: "error", message: json.message });
      }
    } catch (err) {
      setBidStatus({ type: "error", message: "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  // console.log("auction.details.currency", auction.details.currency);
  

  if (loading) return <div className="mt-5">Loading...</div>;
  if (error) return <div className="mt-5 text-danger">Error: {error}</div>;
  if (!auction) return <div className="mt-5">No auction found.</div>;

  return (
    <section className="sec_pd">
      <Fadeup delay={0.3}>
        <h3 className="text-start mt-5 mb-3 mx-3 pb-2 text-black fs_40">
          {auction.title}
        </h3>
      </Fadeup>

      <Fadeup delay={0.2}>
        {auction.details && (
          <p className="text-start mb-4 mx-3 border-bottom pb-2 text-muted">
            {auction.details.description}
          </p>
        )}
      </Fadeup>

      <div className="mt-5 text-dark mb-5">
        {auction.details && (
          
          <>
            <div className="border p-4" style={{ borderRadius: "20px" }}>
              <strong>
                <h4 className="my-4">Details</h4>
              </strong>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <strong>Artist:</strong>
                  <p>{auction.details.artist}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Material:</strong>
                  <p>{auction.details.material}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Carbon Dating:</strong>
                  <p>{auction.details.carbon_dating}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Dimensions (HxWxD):</strong>
                  <p>
                    {auction.details.height} x {auction.details.width} x{" "}
                    {auction.details.depth} {auction.details.measurement_units}
                  </p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Origin:</strong>
                  <p>{auction.details.item_origin}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Ownership History:</strong>
                  <p>{auction.details.ownership_history}</p>
                </div>
                <div className="col-md-4 mb-3">
                  <strong>Price:</strong>
                  <p>
                    {auction.details.price} {auction.details.currency}
                  </p>
                </div>

                {auction.details.certificate1 && (
                  <div className="col-md-4 mb-3">
                    <strong>Certificate 1:</strong>
                    <br />
                    <a
                      href={auction.details.certificate1}
                      className="btn btn-golden-glass text-white mt-2"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View PDF
                    </a>
                  </div>
                )}
                {auction.details.certificate2 && (
                  <div className="col-md-4 mb-3">
                    <strong>Certificate 2:</strong>
                    <br />
                    <a
                      href={auction.details.certificate2}
                      className="btn btn-golden-glass text-white mt-2"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View PDF
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* 🏆 Highest Bid Section */}
            <div
              className="border p-4 mt-5 text-center bg-light"
              style={{ borderRadius: "20px" }}
            >
              <h4 className="mb-3 text-dark">🏆 Highest Bid</h4>
              {highestBid ? (
                <p className="fs-4 fw-bold text-success">
                  {auction.details.currency} - {highestBid.current_highest_bid} <br />
                  <small className="text-muted">
                    {highestBid.user_name || "Anonymous"}
                  </small>
                </p>
              ) : (
                <p className="text-muted">No bids yet. Be the first to bid!</p>
              )}
              {isLoggedIn ? (
                <button 
                  onClick={() => setShowBidDialog(true)} 
                  className="btn btn-golden-glass text-white mt-2"
                >
                  Place Bid
                </button>
              ) : (
                <div className="alert alert-info">
                  Please <a href="/login">login</a> or <a href="/register">register</a> to place a bid
                </div>
              )}
            </div>
          </>
        )}

        {/* 💬 Bid Dialog */}
        {showBidDialog && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
            style={{
              background: "rgba(0,0,0,0.5)",
              zIndex: 1000,
            }}
          >
            <div
              className="bg-white p-4 rounded shadow text-center"
              style={{ width: "350px" }}
            >
              <h5 className="mb-3 text-dark">Place Your Bid</h5>
              <input
                type="number"
                className="form-control mb-3"
                placeholder="Enter bid amount"
                value={bidAmount}
                onChange={(e) => setBidAmount(e.target.value)}
              />
              {bidStatus && (
                <p
                  className={`${
                    bidStatus.type === "success" ? "text-success" : "text-danger"
                  } small mb-3`}
                >
                  {bidStatus.message}
                </p>
              )}
              <div className="d-flex justify-content-center gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowBidDialog(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-golden-glass text-white"
                  onClick={handleBidSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Placing..." : "Submit Bid"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🖼️ Images Section */}
        {auction.images?.length > 0 && (
          <>
            <h4 className="mt-4">Images</h4>
            <div className="row g-3 mt-3">
              {auction.images.map((image, index) => (
                <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-4">
                  <img
                    src={image.image}
                    className="img-fluid rounded border p-1"
                    alt={image.image_type}
                    style={{
                      height: "300px",
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default AuctionDetailsComp;
